import React from "react";

import type { ProcessedPickemSelection } from "@/hooks/firestore/betting/useEntries";

import { useTitle } from "@/hooks/graphql/useTitles";
import { Odds } from "@picklebet/ui";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import { formatUserDateTime } from "@/utilities/datetime";
import { HISTORICAL_DATETIME } from "@/utilities/dateTimeFormats";
import { getRaceMarketName } from "@/features/pickems/util/helpers";
import { ReplaceIcon } from "@picklebet/ui";
import { Pill } from "@picklebet/ui";
import { PickemIcon } from "./PickIcon";
import { DrawerLineItem } from "../common/DrawerLineItem";
import { TeamNames } from "../common/TeamNames";
import { PickClamp } from "./PickClamp";
import { Selection } from "../selections/BaseSelection";

import * as styles from "./PickemPick.module.scss";

type PickemSelectionProps = {
  selection: ProcessedPickemSelection;
  entryId: string;
  isFirst?: boolean;
  isLast: boolean;
  isEntryWon?: boolean;
  connectUp?: boolean;
  connectDown?: boolean;
};

const PickemPick = ({
  selection,
  entryId,
  isFirst,
  isLast,
  isEntryWon,
  connectUp = false,
  connectDown = false,
}: PickemSelectionProps) => {
  const title = useTitle(selection.event.sport);

  const isRace = selection.event.eventType === "RACE";
  // only show after the contest is settled
  const replacedPick = selection?.replacementOutcome;
  // only show void or scrachted before race settlement
  const isVoid = selection.result === "VOID" && (!isRace || !replacedPick);
  const validPick = replacedPick || selection;
  const isSelectionWon = validPick.result === "WIN";

  const raceMarketName =
    isRace &&
    (validPick.marketName || getRaceMarketName(validPick.outcomeType));

  const raceName = `${selection.event.attributes.venueName} - R${selection.event.attributes.raceNumber}`;
  const contestName =
    selection.event.attributes.seasonName ??
    selection.event.attributes.tournamentName ??
    "";

  return (
    <Selection
      isVoid={isVoid}
      isFirst={isFirst}
      isLast={isLast}
      isSelectionWon={isSelectionWon}
      isEntryWon={isEntryWon}
      connectUp={connectUp}
      connectDown={connectDown}
      isPickem
      slots={{
        icon: <TitleIcon title={title} />,
        outcomeName: (
          <div className={styles.name}>
            <PickemIcon className={styles.outcomeIcon} pick={validPick} />
            {validPick.outcomeName}
          </div>
        ),
        marketName: (
          <div className={styles.marketNameAndPoints}>
            {raceMarketName ? `${raceMarketName} -` : ""}
            <Odds
              base={
                !replacedPick ? selection.points : replacedPick?.points ?? 0
              }
              format="points"
              isVoid={isVoid}
              removeVoidLabel
            />
            {!!replacedPick && (
              <Pill className={styles.replacement} size="small">
                <ReplaceIcon />
                replacement
              </Pill>
            )}
          </div>
        ),
        clamp: <PickClamp selection={selection} isEntryWon={isEntryWon} />,
        drawerContent: (
          <>
            {selection.event.scheduledStartTime && (
              <DrawerLineItem
                label="Time"
                value={formatUserDateTime(
                  selection.event.scheduledStartTime,
                  HISTORICAL_DATETIME,
                )}
              />
            )}
            {!isRace && (
              <DrawerLineItem
                label="Match"
                value={<TeamNames eventName={selection.event.eventName} />}
              />
            )}
            <DrawerLineItem
              label={isRace ? "Event" : ""}
              value={isRace ? raceName : contestName}
            />
            <DrawerLineItem label="Entry Id" value={entryId} />
          </>
        ),
      }}
    />
  );
};

export { PickemPick };
